import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header/Header";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { AuthModalProvider } from "./context/AuthModalContext";
import { CartModalProvider } from "./context/CartModalContext";
import { ProductsProvider } from "./context/ProductsContext";
import { PrivateRoute } from "./components/PrivateRoute";
import { SeoManager } from "./components/SeoManager";
import { SeoProvider } from "./context/SeoContext";
import { LocaleRouterSync } from "./components/LocaleRouterSync";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { DEFAULT_LOCALE } from "./utils/localeRouting";

const Home = lazy(() => import("./pages/Home").then((module) => ({ default: module.Home })));
const QuemSomos = lazy(() =>
  import("./pages/QuemSomos").then((module) => ({ default: module.QuemSomos }))
);
const Products = lazy(() =>
  import("./pages/Products").then((module) => ({ default: module.Products }))
);
const DownloadArea = lazy(() =>
  import("./pages/DownloadArea").then((module) => ({ default: module.DownloadArea }))
);
const Recruitment = lazy(() =>
  import("./pages/Recruitment").then((module) => ({ default: module.Recruitment }))
);
const Contacts = lazy(() =>
  import("./pages/Contacts").then((module) => ({ default: module.Contacts }))
);
const JobOffer = lazy(() =>
  import("./pages/JobOffer").then((module) => ({ default: module.JobOffer }))
);
const Account = lazy(() =>
  import("./pages/Account").then((module) => ({ default: module.Account }))
);
const Complaint = lazy(() =>
  import("./pages/Complaint").then((module) => ({ default: module.Complaint }))
);
const News = lazy(() =>
  import("./pages/News").then((module) => ({ default: module.News }))
);
const IndividualNews = lazy(() =>
  import("./pages/IndividualNews").then((module) => ({ default: module.IndividualNews }))
);
const ResetPassword = lazy(() =>
  import("./pages/ResetPassword").then((module) => ({ default: module.ResetPassword }))
);

const queryClient = new QueryClient();

const AppShell = () => (
  <div className="App">
    <ScrollToTop />
    <LocaleRouterSync />
    <SeoManager />
    <AuthModalProvider>
      <Header />
    </AuthModalProvider>
    <Suspense fallback={<LoadingSpinner size={24} />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/products/*"
          element={
            <ProductsProvider>
              <Products />
            </ProductsProvider>
          }
        />
        <Route path="/aboutus" element={<QuemSomos />} />
        <Route path="/downloadarea" element={<DownloadArea />} />
        <Route path="/recruitment" element={<Recruitment />} />
        <Route path="/recruitment/*" element={<JobOffer />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/complaint" element={<Complaint />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:slug" element={<IndividualNews />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route
          path="/account/*"
          element={
            <PrivateRoute>
              <Account />
            </PrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to="." replace />} />
      </Routes>
    </Suspense>
    <Footer />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <CartModalProvider>
          <Router>
            <QueryClientProvider client={queryClient}>
              <SeoProvider>
                <Routes>
                  <Route path="/" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />
                  <Route path="/:lang/*" element={<AppShell />} />
                  <Route path="*" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />
                </Routes>
              </SeoProvider>
            </QueryClientProvider>
          </Router>
        </CartModalProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
